import './index.css'
import { EditorState, Modifier, RichUtils, SelectionState } from 'draft-js'
import {
  getEntityRange,
  getSelectedBlocksMetadata,
  getSelectionEntity,
  getSelectionText,
  setBlockData,
} from 'draftjs-utils'
import get from 'lodash/get'
import * as React from 'react'
import { ENTITY_TYPE } from '@disco/constants/content-editor'
import {
  getCurrentEntity,
  getEntityTypeByEntityKey,
  insertButton,
  insertImage,
  insertLink,
  insertMergeTag,
  removeLink,
} from '@disco/content-editor/editor-utils'
import { getKey, getKeyValue } from '@disco/content-editor/inline-style'
import { linkify } from '@disco/utils/links'
import {
  removeAllInlineStyles,
  toggleCustomInlineStyle,
} from 'components/common/rich-text-editor/utils/toggle-custom-inline-style'
import Dropdown from 'components/ui/dropdowns/native-dropdown'
import {
  ALIGNMENT_TYPES,
  BLOCK_TYPES,
  COLOR_TYPES,
  ENTITY_TYPES,
  FONT_SIZE_TYPES,
  INLINE_ACTIONS,
  INLINE_TYPES,
  MERGE_TAG_TYPES,
  TEXT_TYPES,
} from './constants'
import RichTextToolbarButton from './toolbar-button'
import Icon, { IconKey } from 'components/ui/icon'

type OwnProps = {
  editorState: EditorState
  customStyleMap: Record<string, any>
  onChange: (
    editorState: EditorState,
    customStyleMap?: Record<string, any>,
  ) => any
  onStyleMapUpdate: (update: Record<string, any>) => any
  maxImageWidth?: number
}
type OwnState = {
  currentInlineStyle: any
  currentBlockType: string
  currentBlockTextAlignment: string
  currentEntity: any
  currentEntityText: string
}

const getEntityOptions = (currentEntity, currentEntityText) => {
  if (currentEntity) {
    return {
      caption:
        currentEntity.getType() === 'LINK' ? currentEntityText : undefined,
      ...currentEntity.getData(),
    }
  }

  if (currentEntityText)
    return {
      caption: currentEntityText,
      target: '_blank',
      url: '',
    }
  return null
}

const getColorOptions = (currentInlineStyle, style) => {
  if (!currentInlineStyle) return null
  const color = getKey(currentInlineStyle, style)
  let colorValue: string | null | undefined = null

  if (style === 'bgcolor') {
    colorValue = getKeyValue('bgcolor-', color)
  } else if (style === 'color') {
    colorValue = getKeyValue('color-', color)
  }

  return {
    color: colorValue,
  }
}

const getDefaultOptions = (
  style: string,
  getOptions?: (style: string) => Record<string, any> | null | undefined,
) => {
  let defaultOptions

  if (style === 'image') {
    defaultOptions = {
      src: '',
    }
  } else if (style === 'button') {
    defaultOptions = {
      link: 'https://disco.ac',
      caption: 'Button',
      style: {
        backgroundColor: '#0693e3',
        color: 'white',
      },
    }
  } else if (style === 'link') {
    defaultOptions = {
      url: '',
      target: '_blank',
      caption: '',
      ...(getOptions ? getOptions(style) : {}),
    }
  } else if (style === 'color' || style === 'bgcolor') {
    defaultOptions = {
      color: null,
      ...(getOptions ? getOptions(style) : {}),
    }
  }

  return defaultOptions
}

/**
 * RichTextToolbar component for providing rich text editing controls
 *
 * A comprehensive toolbar component that provides all formatting controls for the Draft.js
 * rich text editor. Includes text styling, formatting, alignment, colors, inline styles,
 * entity insertion (images, buttons, links), and merge tags. Manages editor state updates
 * and provides configuration interfaces for complex elements.
 *
 * @param props - Component props
 * @param props.editorState - Current Draft.js editor state
 * @param props.customStyleMap - Custom styling definitions
 * @param props.onChange - Callback for editor state changes
 * @param props.onStyleMapUpdate - Callback for custom style updates
 * @param props.maxImageWidth - Maximum width for inserted images
 * @returns JSX element containing the complete formatting toolbar
 */
export default class RichTextToolbar extends React.Component<
  OwnProps,
  OwnState
> {
  state: OwnState = {
    currentInlineStyle: null,
    currentBlockType: '',
    currentBlockTextAlignment: '',
    currentEntity: null,
    currentEntityText: '',
  }
  onToggleInlineStyles: (label: string) => void = (label: string) => {
    const { editorState } = this.props
    const editorStateFocused = EditorState.forceSelection(
      editorState,
      editorState.getSelection(),
    )
    this.props.onChange(RichUtils.toggleInlineStyle(editorStateFocused, label))
  }
  onInlineAction: () => void = () => {
    const { editorState, customStyleMap } = this.props
    let editorStateFocused = EditorState.forceSelection(
      editorState,
      editorState.getSelection(),
    )
    this.props.onChange(
      removeAllInlineStyles(editorStateFocused, customStyleMap),
    )
  }
  onToggleFontSizeStyle: (styleValue: string) => void = (
    styleValue: string,
  ) => {
    const { editorState, customStyleMap } = this.props
    let editorStateFocused = EditorState.forceSelection(
      editorState,
      editorState.getSelection(),
    )
    this.props.onChange(
      toggleCustomInlineStyle(
        editorStateFocused,
        customStyleMap,
        'fontSize',
        styleValue,
      ),
    )
  }
  onToggleColorStyle: (styleValue: string, color?: any) => void = (
    styleValue: string,
    color?: Record<string, any>,
  ) => {
    if (!color) return
    const { editorState, customStyleMap } = this.props
    let editorStateFocused = EditorState.forceSelection(
      editorState,
      editorState.getSelection(),
    )
    this.props.onStyleMapUpdate({
      [`${styleValue}-${color.hex}`]: {
        [styleValue === 'color' ? 'color' : 'backgroundColor']: color.hex,
      },
    })
    this.props.onChange(
      toggleCustomInlineStyle(
        editorStateFocused,
        customStyleMap,
        styleValue,
        color.hex,
      ),
    )
  }
  onToggleBlockType: (label: string) => void = (label: string) => {
    this.props.onChange(
      RichUtils.toggleBlockType(this.props.editorState, label),
    )
  }
  onToggleAlignment: (label: string) => any | void = (label: string) => {
    const { editorState } = this.props
    const currentTextAlignment =
      getSelectedBlocksMetadata(editorState).get('text-align')
    const alignment = currentTextAlignment !== label ? label : undefined
    const newState: EditorState = setBlockData(editorState, {
      'text-align': alignment,
    })
    const { currentEntity } = this.state

    if (currentEntity) {
      const currentEntityKey = getSelectionEntity(newState)
      const entityType = getEntityTypeByEntityKey(newState, currentEntityKey)

      if (
        entityType === ENTITY_TYPE.IMAGE ||
        entityType === ENTITY_TYPE.BUTTON
      ) {
        const contentState = newState.getCurrentContent()
        let align = alignment ? alignment.replace('align-', '') : undefined
        if (align === 'justify') align = 'left'
        const newContentState = contentState.mergeEntityData(currentEntityKey, {
          align,
        })
        return this.props.onChange(
          EditorState.push(newState, newContentState, 'apply-entity'),
        )
      }
    }

    this.props.onChange(newState)
  }
  onToggleInsert: (label: string, options?: any) => void = (
    label: string,
    options?: any,
  ) => {
    const { editorState } = this.props
    let newState = editorState

    if (label === 'image') {
      newState = insertImage(editorState, options)
    } else if (label === 'button') {
      newState = insertButton(editorState, options)
    } else if (label === 'link') {
      newState = insertLink(editorState, options)
    } else if (label === 'remove-link') {
      newState = removeLink(editorState)
    } else if (label === 'merge-tag') {
      newState = insertMergeTag(editorState, options)
    }

    this.props.onChange(newState)
  }
  _handleConfigSubmit: (value: any) => void = (value: Record<string, any>) => {
    const { editorState } = this.props
    const selection = editorState.getSelection()
    let content = editorState.getCurrentContent()
    const block = content.getBlockForKey(selection.getStartKey())
    const entityKey = getSelectionEntity(editorState)
    let newState = editorState
    let textValue = ' '

    if (typeof value.caption === 'string') {
      textValue = value.caption
    }

    const { currentEntity } = this.state
    const range = getEntityRange(editorState, entityKey)
    let offsets: {
      startOffset?: number
      endOffset?: number
    } = {}

    if (
      ((currentEntity && currentEntity.getType() === 'LINK') ||
        !currentEntity) &&
      range
    ) {
      const { start, end } = range
      offsets = {
        startOffset: start,
        endOffset: end,
      }
    }

    const selectionState = new SelectionState({
      anchorKey: block.getKey(),
      anchorOffset: offsets.startOffset || 0,
      focusKey: block.getKey(),
      focusOffset: offsets.endOffset || block.getLength(),
      ...offsets,
    })
    content = Modifier.replaceText(
      content,
      selectionState,
      textValue,
      {},
      entityKey,
    )
    newState = EditorState.push(newState, content, 'insert-characters')
    // note: `mergeEntityData` mutates `editorState`
    // https://github.com/facebook/draft-js/issues/940
    content = content.mergeEntityData(entityKey, value)
    this.props.onChange(newState)
  }
  _renderButtons: (
    list: Array<{
      ConfigComponent?: any
      icon?: IconKey
      label: string
      style: string
      title?: string
    }>,
    handler: (style: string, options?: any) => unknown,
    activeChecker: (style: string) => boolean,
    getOptions?: (style: string) => any | null | undefined,
  ) => Array<React.ReactNode> = (
    list: {
      label: string
      style: string
      icon?: IconKey
      title?: string
      ConfigComponent?: (...args: Array<any>) => any
    }[],
    handler: (style: string, options?: Record<string, any>) => unknown,
    activeChecker: (style: string) => boolean,
    getOptions?: (style: string) => Record<string, any> | null | undefined,
  ): React.ReactNode[] => {
    return list.map(({ label, title, style, icon, ConfigComponent }) => {
      const active = activeChecker(style)
      let defaultOptions = getDefaultOptions(style, getOptions)
      const Component = ConfigComponent ? (
        <ConfigComponent
          value={active && getOptions ? getOptions(style) : defaultOptions}
          maxImageWidth={
            style === 'image' ? this.props.maxImageWidth : undefined
          }
          onSubmit={nextOptions => {
            if (active) {
              // edit block
              this._handleConfigSubmit(nextOptions)
            } else {
              // create block
              handler(style, nextOptions)
            }
          }}
        />
      ) : null
      return (
        <RichTextToolbarButton
          label={label}
          style={style}
          active={active}
          key={label}
          icon={icon}
          title={title || label}
          onClick={() => handler(style)}
          configComponent={Component}
          popupStyle={
            style === 'color' || style === 'bgcolor'
              ? 'rich-text-toolbar__button__config--color'
              : undefined
          }
        />
      )
    })
  }
  _renderDropdown: (
    caption: string,
    list: Array<{
      label: string
      style: string
    }>,
    handler: (style: string, options?: any) => unknown,
    activeChecker: (style: string) => boolean,
  ) => React.ReactNode = (
    caption: string,
    list: {
      label: string
      style: string
    }[],
    handler: (style: string, options?: Record<string, any>) => unknown,
    activeChecker: (style: string) => boolean,
  ) => {
    const options = list.map(({ label, style }) => ({
      value: style,
      label,
    }))
    const chosenOption = list.find(({ style }) => activeChecker(style))
    return (
      <Dropdown
        className="SelectDropdown toolbar-dropdown"
        caption={caption}
        options={options}
        value={get(chosenOption, 'style', '')}
        onChange={e => handler(e.target.value)}
      />
    )
  }

  UNSAFE_componentWillReceiveProps({ editorState }: OwnProps) {
    if (this.props.editorState === editorState) return
    const currentInlineStyle = editorState.getCurrentInlineStyle()
    const selection = editorState.getSelection()
    const content = editorState.getCurrentContent()
    const currentBlockType = content
      .getBlockForKey(selection.getStartKey())
      .getType()
    const currentBlockTextAlignment =
      getSelectedBlocksMetadata(editorState).get('text-align')
    const currentEntity = getCurrentEntity(editorState)
    const selectionStartBlock = content.getBlockForKey(selection.getStartKey())
    const selectionEndBlock = content.getBlockForKey(selection.getEndKey())
    const currentBlock =
      selectionStartBlock === selectionEndBlock ? selectionStartBlock : null
    let currentEntityText = ''

    if (currentEntity) {
      const currentEntityKey = getSelectionEntity(editorState)
      const range = getEntityRange(editorState, currentEntityKey)

      if (range) {
        const { start, end } = range
        const text = currentBlock ? currentBlock.getText() : ''
        currentEntityText = text.substring(start, end)
      }
    } else if (
      typeof selection.anchorOffset === 'number' &&
      typeof selection.focusOffset === 'number' &&
      selection.anchorOffset !== selection.focusOffset
    ) {
      currentEntityText = getSelectionText(editorState)
    }

    this.setState({
      currentInlineStyle,
      currentBlockType,
      currentBlockTextAlignment,
      currentEntity,
      currentEntityText,
    })
  }

  render(): React.ReactElement<'div'> {
    const {
      currentBlockType,
      currentInlineStyle,
      currentBlockTextAlignment,
      currentEntity,
      currentEntityText,
    } = this.state
    return (
      <div className="rich-text-toolbar">
        {this._renderDropdown(
          'Text style',
          TEXT_TYPES,
          this.onToggleBlockType,
          style => style === currentBlockType,
        )}
        {this._renderDropdown(
          'Size',
          FONT_SIZE_TYPES,
          this.onToggleFontSizeStyle,
          style => {
            return currentInlineStyle
              ? currentInlineStyle.has(`fontsize-${style}`)
              : false
          },
        )}
        {this._renderButtons(
          COLOR_TYPES,
          this.onToggleColorStyle,
          () => {
            return false
          },
          style => getColorOptions(currentInlineStyle, style),
        )}
        {this._renderButtons(INLINE_ACTIONS, this.onInlineAction, () => false)}
        {this._renderButtons(
          BLOCK_TYPES,
          this.onToggleBlockType,
          style => style === currentBlockType,
        )}
        {this._renderButtons(INLINE_TYPES, this.onToggleInlineStyles, style =>
          currentInlineStyle ? currentInlineStyle.has(style) : false,
        )}
        {this._renderButtons(
          ALIGNMENT_TYPES,
          this.onToggleAlignment,
          style => style === currentBlockTextAlignment,
        )}
        {this._renderButtons(
          ENTITY_TYPES,
          this.onToggleInsert,
          style => {
            if (!currentEntity) return false
            const type = (currentEntity.getType() || '').toLowerCase()
            return (
              type === style ||
              (`remove-${type}` === style && !linkify.match(currentEntityText))
            )
          },
          () => getEntityOptions(currentEntity, currentEntityText),
        )}
        {this._renderDropdown(
          'Merge tags',
          MERGE_TAG_TYPES,
          (label: string) =>
            this.onToggleInsert('merge-tag', {
              type: label,
              fallback: '',
            }),
          style => {
            if (!currentEntity) return false
            const type = (currentEntity.getType() || '').toLowerCase()
            return type === style
          },
        )}
      </div>
    )
  }
}
